import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Reel } from '@eprintsmart/shared';
import { createReel, deleteReel, updateReel, watchReels } from '../services/reels';

/** Manage the shoppable reels on the storefront home. Upload a vertical video
 * (and an optional poster), give it a label, and link the product it sells. */
export function Reels() {
  const [rows, setRows] = useState<Reel[]>([]);
  useEffect(() => watchReels(setRows), []);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Reels</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Short vertical videos shown on the home page. Tapping one opens a player with an “Order this”
        button linking to the product. Portrait (9:16) videos look best.
      </Typography>

      <NewReel nextOrder={(rows[rows.length - 1]?.order ?? 0) + 1} />

      <Stack spacing={1.5} sx={{ mt: 3 }}>
        {rows.length === 0 ? (
          <Typography color="text.secondary">No reels yet.</Typography>
        ) : (
          rows.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="video"
                src={r.videoUrl}
                poster={r.posterUrl}
                muted
                sx={{ width: 54, height: 96, objectFit: 'cover', borderRadius: 1, bgcolor: '#000', flexShrink: 0 }}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography fontWeight={600}>{r.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.productSlug ? `→ ${r.productSlug}` : 'no product linked'} · order {r.order}
                </Typography>
              </Box>
              <Chip size="small" label={r.active ? 'Live' : 'Hidden'} color={r.active ? 'success' : 'default'} />
              <Switch checked={r.active} onChange={(e) => void updateReel(r.id, { active: e.target.checked })} />
              <IconButton onClick={() => { if (confirm('Delete this reel?')) void deleteReel(r.id); }}>
                <DeleteOutlineIcon />
              </IconButton>
            </Paper>
          ))
        )}
      </Stack>
    </Box>
  );
}

function NewReel({ nextOrder }: { nextOrder: number }) {
  const [label, setLabel] = useState('');
  const [productSlug, setProductSlug] = useState('');
  const [order, setOrder] = useState(nextOrder);
  const [video, setVideo] = useState<File | null>(null);
  const [poster, setPoster] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const posterRef = useRef<HTMLInputElement>(null);

  useEffect(() => setOrder(nextOrder), [nextOrder]);

  const save = async () => {
    if (!label.trim() || !video) return;
    setSaving(true);
    setError(null);
    try {
      await createReel({
        label: label.trim(),
        productSlug: productSlug.trim() || undefined,
        order,
        videoFile: video,
        posterFile: poster ?? undefined,
      });
      setLabel(''); setProductSlug(''); setVideo(null); setPoster(null);
      if (videoRef.current) videoRef.current.value = '';
      if (posterRef.current) posterRef.current.value = '';
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Upload failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Add a reel</Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} fullWidth required />
          <TextField
            label="Product slug (for “Order this”)" placeholder="e.g. standard-visiting-cards"
            value={productSlug} onChange={(e) => setProductSlug(e.target.value)} fullWidth
          />
          <TextField
            label="Order" type="number" value={order} onChange={(e) => setOrder(Number(e.target.value) || 0)}
            sx={{ width: 110 }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Button variant="outlined" component="label">
            {video ? `Video: ${video.name}` : 'Choose video *'}
            <input ref={videoRef} type="file" accept="video/*" hidden onChange={(e) => setVideo(e.target.files?.[0] ?? null)} />
          </Button>
          <Button variant="outlined" component="label">
            {poster ? `Poster: ${poster.name}` : 'Poster image (optional)'}
            <input ref={posterRef} type="file" accept="image/*" hidden onChange={(e) => setPoster(e.target.files?.[0] ?? null)} />
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={() => void save()} disabled={saving || !label.trim() || !video}>
            {saving ? 'Uploading…' : 'Add reel'}
          </Button>
        </Stack>
        {error && <Typography color="error" variant="body2">{error}</Typography>}
      </Stack>
    </Paper>
  );
}
